<template>
  <div class="w-full h-full flex">
    <div
      v-for="index in hours"
      :key="index"
      :class="[
        dynamicClass,
        {
          'boardTimeLine__boxes--endOfDay': shouldShowDayName(index - 1, true)
        }
      ]"
      :style="boxStyle"
    >
      <div
        v-if="header"
        class="px-2 font-semibold flex flex-col relative"
        :class="{
          'boardTimeLine__boxes__header--endOfDay': shouldShowDayName(index - 1, true)
        }"
      >
        <div v-if="shouldShowDayName(index - 1)" class="h-6">
          <span v-if="index === 1" class="whitespace-nowrap">{{ startDateLabel }}</span>
          <span v-else class="whitespace-nowrap">{{ deriveDateLabel(index) }}</span>
        </div>
        <div v-else-if="shouldShowDayName(index - 1, true)" class="h-6 relative">
          <div class="whitespace-nowrap absolute right-0">
            {{ deriveDateLabel(index) }}
          </div>
          <div class="h-6 boardTimeLine--lastHour">
            {{ getTime(index - 1, true) }}
          </div>
        </div>
        <div v-else class="h-6"></div>
        <div class="h-6">
          {{ getTime(index - 1) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { format, add, addBusinessDays } from "date-fns";
import { computed, defineComponent } from "vue";
import { dateLocale } from "@/use/useDate";
import { useBoardStore } from "@/store/board/BoardStore";
import { i18n } from "@/i18n/i18n";

export default defineComponent({
  name: "BoardTimeBoxes",
  props: {
    header: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const boardStore = useBoardStore();

    const dateLabelFormat = "E dd.MM.yyyy";

    // Refs which we need
    const hoursDifference = computed(() => boardStore.hoursDifference);

    const hours = computed(() => {
      return Math.round(hoursDifference.value);
    });

    const filteredHoursAmount = computed(() => boardStore.filteredHoursAmount);
    const fromTime = computed(() => boardStore.fromTime);
    const boxWidth = computed(() => boardStore.hourBoxWidth);
    const minWidth = computed(() => boardStore.hourBoxMinWidth);
    const extraMinWidth = computed(() => boardStore.hourBoxExtraMinWidth);
    const isZeroZoomLevel = computed(() => minWidth.value === extraMinWidth.value);
    const showWeekends = computed(() => boardStore.showWeekends);

    const boxStyle = computed(() => {
      return { width: `${boxWidth.value}%`, minWidth: `${minWidth.value}rem` };
    });

    const startDate = computed(() => boardStore.fromDate);

    const startDateLabel = computed(() => {
      return format(startDate.value, dateLabelFormat, {
        locale: dateLocale(i18n.locale)
      });
    });

    //dynamic CSS returns class
    const dynamicClass = computed(() => {
      if (props.header) {
        return "boardTimeLine__boxes--header";
      }
      return isZeroZoomLevel.value ? "boardTimeLine__boxes__0zoom" : "boardTimeLine__boxes";
    });

    /**
     * Logic for showing the time on the header timeline
     */
    let timeCounter = 0;
    const getTime = (index: number, isDayEnd?: boolean): string => {
      timeCounter = index % filteredHoursAmount.value === 0 ? 0 : ++timeCounter;

      if ((index + 1) % 3 === 0 && !isDayEnd) {
        return "";
      }

      if (isZeroZoomLevel.value && !shouldShowDayName(index, isDayEnd)) {
        return "";
      }

      const checkedNumber = index % 3 === 0;
      const isLastHourOfDay = (index + 1) % filteredHoursAmount.value === 0;
      if (checkedNumber) {
        const date = new Date();
        date.setHours(fromTime.value + timeCounter, 0, 0, 0);
        return format(date, "H:00");
      } else if (isLastHourOfDay) {
        const date = new Date();
        date.setHours(fromTime.value + timeCounter + 1, 0, 0, 0);
        return format(date, "H:00");
      }
      return "";
    };

    /**
     * Checks if it should show the day start/end date/day name label
     * @param hourBoxes - hour box index counting from the board start
     * @param isDayEnd - last hour box of a day flag
     */
    const shouldShowDayName = (hourBoxes: number, isDayEnd?: boolean): boolean => {
      if (isDayEnd) {
        hourBoxes++;
      }
      if (hourBoxes === 0) {
        return true;
      }
      return hourBoxes % filteredHoursAmount.value === 0;
    };

    const deriveDateLabel = (hourBoxIndex: number): string => {
      const daysToAdd = Math.floor((hourBoxIndex - 1) / filteredHoursAmount.value);
      const dateToDisplay = showWeekends.value
        ? add(startDate.value, { days: daysToAdd })
        : addBusinessDays(startDate.value, daysToAdd);

      return format(dateToDisplay, dateLabelFormat, {
        locale: dateLocale(i18n.locale)
      });
    };

    return {
      boxWidth,
      boxStyle,
      hours,
      dynamicClass,
      startDateLabel,
      deriveDateLabel,
      getTime,
      shouldShowDayName
    };
  }
});
</script>

<style scoped lang="scss">
@use "TimeLineBoxes";
</style>
